// Production Fill Plan — the plant team's jumping-off point for the CPLEX
// daily solve (PJ + Scott, meetings 2026-06-26 / 06-29, kanban bb4492c8):
// finished goods × Day 1..5 fill, grouped by department, priority-desc
// (MAX order score across a row's contributing orders), NO customer columns.
// The CPLEX Modeler chat rides alongside for "fix it and recalculate" asks;
// the Sankey is one button away (secondary view).
//
// Standalone full-viewport page (not wrapped in MainLayout), mirroring
// CplexSankeyFullPage's run-resolution pattern.

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  getRun,
  listRuns,
  TERMINAL_STATES,
  type CplexRun,
  type CplexRunSummary,
} from '../services/cplex';
import {
  aggregateSkus,
  applyFillPlanFilters,
  businessDays,
  dayTotals,
  departmentsOf,
  planRows,
  scoresAreDifferentiated,
} from '../components/cplex/fillPlan/fillPlanHelpers';
import { FillPlanTable } from '../components/cplex/fillPlan/FillPlanTable';
import { FillPlanControls } from '../components/cplex/fillPlan/FillPlanControls';
import { ContributingOrdersPanel } from '../components/cplex/fillPlan/ContributingOrdersPanel';
import {
  EMPTY_FILL_PLAN_FILTERS,
  type FillPlanFilterState,
} from '../components/cplex/fillPlan/types';
import { CplexModelerChatPanel } from '../components/cplex/CplexModelerChatPanel';
import { GuruFeedbackWidget } from '../components/layout/GuruFeedbackWidget';
import styles from './CplexFillPlanPage.module.css';

const fmt = (n: number) => Math.round(n).toLocaleString('en-US');

export function CplexFillPlanPage() {
  const params = useParams<{ runId?: string }>();
  const [runs, setRuns] = useState<CplexRunSummary[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(
    params.runId ?? null,
  );
  const [run, setRun] = useState<CplexRun | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [filters, setFilters] = useState<FillPlanFilterState>(
    EMPTY_FILL_PLAN_FILTERS,
  );
  const [selectedSku, setSelectedSku] = useState<string | null>(null);
  const [chatOpen, setChatOpen] = useState(false);

  // Latest completed run when none is pinned in the URL.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await listRuns(undefined, 25, 0);
        if (cancelled) return;
        const completed = res.items.filter(
          (r) => TERMINAL_STATES.has(r.state) && r.finished_at,
        );
        setRuns(completed);
        if (!selectedRunId && completed.length > 0) {
          setSelectedRunId(completed[0].id);
        }
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedRunId) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    (async () => {
      try {
        // Lean fetch: just the long table + the order-score enrichment.
        const data = await getRun(
          selectedRunId,
          'mfg_and_fill_summary_long',
          undefined,
          'scores',
        );
        if (!cancelled) setRun(data);
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedRunId]);

  const todayIso = useMemo(
    () => (run?.finished_at ?? new Date().toISOString()).slice(0, 10),
    [run?.finished_at],
  );
  const days = useMemo(() => businessDays(todayIso, 5), [todayIso]);
  const scoresLive = useMemo(
    () => scoresAreDifferentiated(run?.order_scores),
    [run?.order_scores],
  );
  const allSkus = useMemo(
    () =>
      aggregateSkus(planRows(run?.result_tables), run?.order_scores, todayIso),
    [run?.result_tables, run?.order_scores, todayIso],
  );
  const skus = useMemo(
    () => applyFillPlanFilters(allSkus, filters),
    [allSkus, filters],
  );
  const departments = useMemo(() => departmentsOf(allSkus), [allSkus]);
  const totals = useMemo(() => dayTotals(allSkus), [allSkus]);
  const selected = useMemo(
    () => allSkus.find((s) => s.sku === selectedSku) ?? null,
    [allSkus, selectedSku],
  );

  const lateCount = allSkus.filter((s) => s.daysLate > 0).length;
  const critCount = allSkus.filter((s) => s.criticalPo).length;

  return (
    <div className={styles.page}>
      <header className={styles.hd}>
        <div>
          <h1 className={styles.title}>Production Fill Plan</h1>
          <div className={styles.sub}>
            Finished goods × Day 1–5 fill · grouped by department · priority
            order{scoresLive ? ' (order score)' : ' (urgency — flat scores this run)'}
          </div>
        </div>
        <div className={styles.hdRight}>
          {run && (
            <>
              <span className={styles.pill}>
                Solved{' '}
                <b>
                  {run.finished_at
                    ? new Date(run.finished_at).toLocaleString()
                    : '—'}
                </b>
              </span>
              <select
                className={styles.runPicker}
                value={selectedRunId ?? ''}
                onChange={(e) => {
                  setSelectedRunId(e.target.value);
                  setSelectedSku(null);
                }}
                title="Pick a solve run"
              >
                {runs.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.finished_at
                      ? new Date(r.finished_at).toLocaleString()
                      : r.id.slice(0, 8)}{' '}
                    · {r.state}
                  </option>
                ))}
                {selectedRunId && !runs.some((r) => r.id === selectedRunId) && (
                  <option value={selectedRunId}>
                    {selectedRunId.slice(0, 8)}
                  </option>
                )}
              </select>
            </>
          )}
          <button
            type="button"
            className={chatOpen ? styles.btnOn : styles.btn}
            onClick={() => setChatOpen((v) => !v)}
          >
            💬 Chat
          </button>
          {selectedRunId && (
            <Link className={styles.btn} to={`/cplex/sankey/${selectedRunId}`}>
              Supply-chain Sankey →
            </Link>
          )}
          <button
            type="button"
            className={styles.btn}
            onClick={() => window.print()}
          >
            🖨 Print
          </button>
        </div>
      </header>

      <div className={styles.chips}>
        <span className={styles.chip}>
          <b>{allSkus.length}</b> finished goods to fill
        </span>
        {days.map((d) => (
          <span
            key={d.n}
            className={totals[d.n] > 0 ? styles.chip : styles.chipDim}
          >
            D{d.n} {d.label}: <b>{totals[d.n] > 0 ? fmt(totals[d.n]) : '—'}</b>
          </span>
        ))}
        {lateCount > 0 && (
          <span className={styles.chipBad}>
            <b>{lateCount}</b> past required-by
          </span>
        )}
        {critCount > 0 && (
          <span className={styles.chipWarn}>
            <b>{critCount}</b> waiting on a critical PO
          </span>
        )}
      </div>

      <div className={styles.bar}>
        <FillPlanControls
          filters={filters}
          onChange={setFilters}
          departments={departments}
          days={days}
          scoresLive={scoresLive}
        />
      </div>

      <main className={styles.body}>
        {loading && <div className={styles.state}>Loading fill plan…</div>}
        {!loading && err && <div className={styles.stateErr}>{err}</div>}
        {!loading && !err && run && allSkus.length === 0 && (
          <div className={styles.state}>
            This run carries no <code>mfg_and_fill_summary_long</code> fills
            (older model version?). Pick another run above.
          </div>
        )}
        {!loading && !err && allSkus.length > 0 && (
          <div className={styles.tableWrap}>
            <FillPlanTable
              skus={skus}
              days={days}
              todayIso={todayIso}
              selectedSku={selectedSku}
              onSelectSku={(sku) =>
                setSelectedSku((cur) => (cur === sku ? null : sku))
              }
            />
          </div>
        )}
      </main>

      <footer className={styles.foot}>
        Fill = bulk-tank → package work the optimizer scheduled (stock
        allocations and staged orders are excluded). Click a row for its
        contributing orders. Source: run{' '}
        <b>{selectedRunId ? selectedRunId.slice(0, 8) : '—'}</b> ·{' '}
        <code>mfg_and_fill_summary_long</code> (5-day pass).
      </footer>

      {selected && (
        <ContributingOrdersPanel
          sku={selected}
          onClose={() => setSelectedSku(null)}
        />
      )}
      {chatOpen && (
        <CplexModelerChatPanel onClose={() => setChatOpen(false)} />
      )}
      <GuruFeedbackWidget />
    </div>
  );
}
